import { useState, useMemo } from 'react'
import { fugueLabProjects, PortfolioCategory, PORTFOLIO_CATEGORIES } from '@/data/portfolio'
import { PortfolioCard } from '@/components/PortfolioCard'
import { Testimonials } from '@/components/Testimonials'
import { Button } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { getUtmContentSlug } from '@/lib/utm-utils'
import { trackCalendlyClick } from '@/lib/analytics'

const CALENDLY_BASE = '/book'

/**
 * utm_content slug (from outreach emails) → portfolio item slug that best
 * matches the lead's org type. The matching item is sorted to position 0 so
 * it's the first thing the lead sees after clicking through from an email.
 *
 * Mirrors the drafter's CLIENT_PROOF mapping in
 * `projects/brands/fugue-lab/leadgen/server/shared/src/ai/client-portfolio.ts`.
 */
const UTM_TO_FEATURED_SLUG: Record<string, string> = {
  'church-music-program': 'holy-trinity-covina',
  'music-festival': 'chroma-music-festival',
  'opera-company': 'matt-mueller',
  'wedding-musician': 'matt-mueller',
  'community-choir': 'kaia-string-quartet',
  'community-orchestra': 'kaia-string-quartet',
  'youth-orchestra': 'kaia-string-quartet',
  'chamber-ensemble': 'kaia-string-quartet',
  'jazz-ensemble': 'jonathan-gregoire',
  'piano-studio': 'susan-bengtson-price',
  'music-school': 'sierra-canyon-photo-competition',
  'university-music-dept': 'sierra-canyon-photo-competition',
}

/**
 * Forward the lead's utm_term + utm_content onto the Calendly URL so a
 * booking from this button attributes back to the lead in the CRM via the
 * Calendly webhook (same pattern as WelcomePage).
 */
function useCalendlyUrl(): string {
  return useMemo(() => {
    if (typeof window === 'undefined') return CALENDLY_BASE
    const params = new URLSearchParams(window.location.search)
    const forwarded = new URLSearchParams()
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const value = params.get(key)
      if (value) forwarded.set(key, value)
    }
    const qs = forwarded.toString()
    return qs ? `${CALENDLY_BASE}?${qs}` : CALENDLY_BASE
  }, [])
}

export default function PortfolioPage() {
  const [categoryFilter, setCategoryFilter] = useState<PortfolioCategory | 'all'>('all')
  const breadcrumbs = generateBreadcrumbs('/portfolio', breadcrumbLabels)

  useSEO({
    title: 'Our Work',
    description:
      'Modern websites and digital systems for classical music organizations—from festivals and choirs to ensembles and sacred music programs. View our portfolio.',
    url: '/portfolio',
    canonical: '/portfolio',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Fugue Lab Portfolio',
      description: 'Modern websites and digital systems for classical music organizations',
      url: 'https://fuguelab.com/portfolio',
      publisher: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        url: 'https://fuguelab.com',
      },
    },
  })

  const calendlyUrl = useCalendlyUrl()

  // If the lead arrived via an outreach email, sort the matching case study
  // to position 0 so the proof they came looking for is the first thing they see.
  const featuredSlug = useMemo(() => {
    const slug = getUtmContentSlug()
    return slug ? UTM_TO_FEATURED_SLUG[slug] : undefined
  }, [])

  // Entries without an explicit category are Websites (the majority).
  const categoryOf = (p: (typeof fugueLabProjects)[number]) => p.category ?? PortfolioCategory.Websites

  const filteredProjects = useMemo(() => {
    const base = fugueLabProjects.filter((p) => categoryFilter === 'all' || categoryOf(p) === categoryFilter)
    if (!featuredSlug) return base
    const featured = base.find((p) => p.slug === featuredSlug)
    if (!featured) return base
    return [featured, ...base.filter((p) => p.slug !== featuredSlug)]
  }, [categoryFilter, featuredSlug])

  // Only surface category tabs that actually have entries.
  const availableCategories = useMemo(() => {
    const present = new Set(fugueLabProjects.map(categoryOf))
    return PORTFOLIO_CATEGORIES.filter((c) => present.has(c))
  }, [])

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const projectsRef = useStaggeredGrid<HTMLElement>('.portfolio-card', { stagger: 0.1 })

  return (
    <div className="mx-auto max-w-5xl space-y-12 px-4 py-12">
      {/* Hero Section */}
      <section ref={heroRef} className="space-y-6 text-center">
        <h1 className="text-fluid-h1">Our Work</h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed">
          We specialize in building modern websites and digital systems for classical music organizations—from festivals
          and choirs to ensembles and sacred music programs.
        </p>
      </section>

      <Testimonials />

      {/* Category Filters */}
      <div className="space-y-6">
        {availableCategories.length > 1 && (
          <div>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`rounded-md px-4 py-2 font-medium transition-colors ${
                  categoryFilter === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                All
              </button>
              {availableCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setCategoryFilter(category)}
                  className={`rounded-md px-4 py-2 font-medium transition-colors ${
                    categoryFilter === category
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results count */}
        <div className="text-muted-foreground text-center text-sm">
          Showing {filteredProjects.length} of {fugueLabProjects.length} projects
        </div>
      </div>

      {/* Projects Masonry Grid */}
      {filteredProjects.length > 0 ? (
        <section ref={projectsRef} className="grid max-w-6xl grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => (
            <PortfolioCard key={project.slug} {...project} className="portfolio-card" />
          ))}
        </section>
      ) : (
        <div className="py-12 text-center">
          <p className="text-muted-foreground">
            No projects found matching your filters. Try adjusting your search criteria.
          </p>
        </div>
      )}

      {/* CTA Section */}
      <section className="border-border/50 space-y-4 border-t py-8 text-center">
        <h2 className="text-xl font-semibold">Like what you see?</h2>
        <p className="text-muted-foreground">Let's talk about how we can help your organization.</p>
        <a
          href={calendlyUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackCalendlyClick('portfolio_footer')}
        >
          <Button size="lg" className="mt-2 cursor-pointer">
            Book a Free Call
          </Button>
        </a>
      </section>
    </div>
  )
}
